import React, { FC, Key, ReactElement, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import Checkbox from '@material-ui/core/Checkbox'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Step from '@material-ui/core/Step'
import { StepIconProps } from '@material-ui/core/StepIcon'
import StepLabel from '@material-ui/core/StepLabel'
import Stepper from '@material-ui/core/Stepper'
import Typography from '@material-ui/core/Typography'
import CheckIcon from '@material-ui/icons/Check'
import { LanguageContext, ReactState } from '../../../../React/Common/Contexts'
import { EMPTY_STRING } from '../../../../React/Common/Constants/Separators'
import {
    executeUserCopyReports,
    getUserCopyReportsReports,
    getUserCopyReportsTargets
} from '../../../../NonReact/common/API/EntityAPIClient'
import CLanguageHandler from '../../../../NonReact/Languages/LanguageHandler'
import { isStringNotEmpty } from '../../../../NonReact/utils/GlobalFunctions'
import { ArrayUtils } from '../../../../NonReact/utils/ArrayUtils'
import {
    UserCopyReportsListResponse,
    UserCopyReportsReportItem,
    UserCopyReportsTargetUserItem
} from '../../../../NonReact/Types/UserCopyReportsTypes'
import { displayError, displaySuccessMessage } from '../../../../React/Common/Utils/Messages'
import { UsersListLanguage } from '../../../../../i18n'
import { DataTable, DataTableColumn } from '../../Molecules/DataTable/DataTable'
import { AdminBackendPermissionNames } from '../../../../React/Common/Constants/Permissions'
import { UsersListRow } from '../../../../NonReact/Types/UsersListTypes'
import { LanguageUtils } from '../../../../React/Common/Utils/LanguageUtils'
import Loader from '../../../../React/Common/Components/Loader/loader'
import { Modal } from '../../Molecules/Modal/Modal'
import { RoundedButton } from '../../Atoms/RoundedButton/RoundedButton'

type CopyReportsReportTableRow = {
    key: Key
    Description: string
    FolderName: string
    ReportName: string
    TypeDescription: string
    ReportId: string
}

type CopyReportsTargetTableRow = {
    key: Key
    Description: string
    Username: string
    FullName: string
}

export type CopyReportsToWizardModalProps = {
    /** Source user row from the application users list (exactly one selected). */
    sourceUser: UsersListRow
    /** Invoked after the dialog has finished its close transition. */
    onExited: () => void
    /** Optional hook after a successful copy (for example refresh the list). */
    onCompleted?: () => void
}

const WIZARD_STEP_COUNT: number = 3
const COPY_REPORTS_WIZARD_CONFIRMATION_CLASS: string = 'copy-reports-wizard-confirmation'
const COPY_REPORTS_WIZARD_TABLE_CLASS = 'copy-reports-wizard-table'
const COPY_REPORTS_WIZARD_CONFIRMATION_TITLE_CLASS = 'copy-reports-wizard-confirmation-title'
const COPY_REPORTS_WIZARD_CONFIRMATION_LIST_CLASS = 'copy-reports-wizard-confirmation-list'
const COPY_REPORTS_WIZARD_STEP_ICON_GLYPH_CLASS = 'copy-reports-wizard-step-icon-glyph'

/**
 * Custom step icon so numbered and completed states use the same contrast treatment.
 *
 * @param props - Step icon state from MUI.
 * @returns Wizard step icon.
 */
const CopyReportsWizardStepIcon: FC<StepIconProps> = ({ active, completed, icon }: StepIconProps): ReactElement => {
    const stepIconClassName: string = [
        'copy-reports-wizard-step-icon',
        active ? 'copy-reports-wizard-step-icon-active' : EMPTY_STRING,
        completed ? 'copy-reports-wizard-step-icon-completed' : EMPTY_STRING
    ]
        .filter(isStringNotEmpty)
        .join(' ')

    return completed ? (
        <span className={stepIconClassName}>
            <CheckIcon className={COPY_REPORTS_WIZARD_STEP_ICON_GLYPH_CLASS} fontSize='small' />
        </span>
    ) : (
        <span className={stepIconClassName}>
            <span className={COPY_REPORTS_WIZARD_STEP_ICON_GLYPH_CLASS}>{icon}</span>
        </span>
    )
}

/**
 * Wizard dialog: pick reports from a source user, pick target users, confirm, then execute the admin copy-reports API.
 *
 * @param props - Modal configuration.
 * @returns Copy-reports wizard dialog element.
 */
export const CopyReportsToWizardModal: FC<CopyReportsToWizardModalProps> = ({
    sourceUser,
    onExited,
    onCompleted
}: CopyReportsToWizardModalProps): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(UsersListLanguage.NAME)

    const [dialogOpen, setDialogOpen]: ReactState<boolean> = useState<boolean>(true)
    const [activeStep, setActiveStep]: ReactState<number> = useState<number>(0)
    const [listLoading, setListLoading]: ReactState<boolean> = useState<boolean>(true)
    const [submitting, setSubmitting]: ReactState<boolean> = useState<boolean>(false)
    const [reportRows, setReportRows]: ReactState<CopyReportsReportTableRow[]> = useState<CopyReportsReportTableRow[]>(
        []
    )
    const [targetRows, setTargetRows]: ReactState<CopyReportsTargetTableRow[]> = useState<CopyReportsTargetTableRow[]>(
        []
    )
    const [selectedReports, setSelectedReports]: ReactState<CopyReportsReportTableRow[]> = useState<
        CopyReportsReportTableRow[]
    >([])
    const [selectedTargets, setSelectedTargets]: ReactState<CopyReportsTargetTableRow[]> = useState<
        CopyReportsTargetTableRow[]
    >([])
    const [overwriteTargetReports, setOverwriteTargetReports]: ReactState<boolean> = useState<boolean>(false)

    const sourceUsername: string = sourceUser.Username
    const sourceDisplayLabel: string = isStringNotEmpty(sourceUser.FullName) ? sourceUser.FullName : sourceUser.Username

    const stepLabels: readonly string[] = useMemo(
        (): readonly string[] => [
            language.get(UsersListLanguage.UsersList_CopyReports_Step_Reports),
            language.get(UsersListLanguage.UsersList_CopyReports_Step_Targets),
            language.get(UsersListLanguage.UsersList_CopyReports_Step_Confirm)
        ],
        [language]
    )

    const reportColumns: DataTableColumn<CopyReportsReportTableRow, keyof CopyReportsReportTableRow>[] = useMemo(
        (): DataTableColumn<CopyReportsReportTableRow, keyof CopyReportsReportTableRow>[] => [
            {
                headerName: language.get(UsersListLanguage.UsersList_CopyReports_Column_Folder),
                field: 'FolderName',
                key: 'copy-reports-folder',
                sortable: true,
                align: 'left'
            },
            {
                headerName: language.get(UsersListLanguage.UsersList_CopyReports_Column_ReportName),
                field: 'ReportName',
                key: 'copy-reports-name',
                sortable: true,
                align: 'left'
            },
            {
                headerName: language.get(UsersListLanguage.UsersList_CopyReports_Column_Type),
                field: 'TypeDescription',
                key: 'copy-reports-type',
                sortable: true,
                align: 'left'
            }
        ],
        [language]
    )

    const targetColumns: DataTableColumn<CopyReportsTargetTableRow, keyof CopyReportsTargetTableRow>[] = useMemo(
        (): DataTableColumn<CopyReportsTargetTableRow, keyof CopyReportsTargetTableRow>[] => [
            {
                headerName: language.get(UsersListLanguage.UsersList_CopyReports_Column_Username),
                field: 'Username',
                key: 'copy-reports-target-username',
                sortable: true,
                align: 'left'
            },
            {
                headerName: language.get(UsersListLanguage.UsersList_CopyReports_Column_FullName),
                field: 'FullName',
                key: 'copy-reports-target-fullname',
                sortable: true,
                align: 'left'
            }
        ],
        [language]
    )

    useEffect((): void => {
        setListLoading(true)

        Promise.all([getUserCopyReportsReports(sourceUsername), getUserCopyReportsTargets(sourceUsername)])
            .then(
                ([reportsResponse, targetsResponse]: [
                    Nullable<UserCopyReportsListResponse<UserCopyReportsReportItem>>,
                    Nullable<UserCopyReportsListResponse<UserCopyReportsTargetUserItem>>
                ]): void => {
                    const reportItems: UserCopyReportsReportItem[] = reportsResponse?.Data?.Items ?? []
                    const targetItems: UserCopyReportsTargetUserItem[] = targetsResponse?.Data?.Items ?? []

                    const nextReportRows: CopyReportsReportTableRow[] = reportItems
                        .filter((item: UserCopyReportsReportItem): boolean =>
                            isStringNotEmpty(item.reportId ?? EMPTY_STRING)
                        )
                        .map(
                            (item: UserCopyReportsReportItem, index: number): CopyReportsReportTableRow => ({
                                key: item.reportId ?? `report-${String(index)}`,
                                Description: item.reportName ?? item.folderName ?? EMPTY_STRING,
                                FolderName: item.folderName ?? EMPTY_STRING,
                                ReportName: item.reportName ?? EMPTY_STRING,
                                TypeDescription: item.reportTypeDescription ?? EMPTY_STRING,
                                ReportId: item.reportId ?? EMPTY_STRING
                            })
                        )

                    const nextTargetRows: CopyReportsTargetTableRow[] = targetItems
                        .filter((item: UserCopyReportsTargetUserItem): boolean =>
                            isStringNotEmpty(item.username ?? EMPTY_STRING)
                        )
                        .map(
                            (item: UserCopyReportsTargetUserItem, index: number): CopyReportsTargetTableRow => ({
                                key: item.username ?? `target-${String(index)}`,
                                Description: item.fullName ?? item.username ?? EMPTY_STRING,
                                Username: item.username ?? EMPTY_STRING,
                                FullName: item.fullName ?? EMPTY_STRING
                            })
                        )

                    setReportRows(nextReportRows)
                    setTargetRows(nextTargetRows)
                }
            )
            .catch(displayError)
            .finally((): void => {
                setListLoading(false)
            })
    }, [sourceUsername])

    const handleCloseDialog = useCallback((): void => {
        setDialogOpen(false)
    }, [])

    const handleNext = useCallback((): void => {
        if (activeStep === 0 && ArrayUtils.isEmpty(selectedReports)) {
            return
        }

        if (activeStep === 1 && selectedTargets.length === 0) return

        setActiveStep((previous: number): number => Math.min(previous + 1, WIZARD_STEP_COUNT - 1))
    }, [activeStep, selectedReports.length, selectedTargets.length])

    const handleBack = useCallback((): void => {
        setActiveStep((previous: number): number => Math.max(previous - 1, 0))
    }, [])

    const handleExecuteCopy = useCallback((): void => {
        const reportIds: string[] = selectedReports
            .map((row: CopyReportsReportTableRow): string => row.ReportId)
            .filter(isStringNotEmpty)
        const targetUsernames: string[] = selectedTargets
            .map((row: CopyReportsTargetTableRow): string => row.Username)
            .filter(isStringNotEmpty)

        if (reportIds.length === 0 || targetUsernames.length === 0) return

        setSubmitting(true)

        executeUserCopyReports(sourceUsername, {
            reportIds,
            targetUsernames,
            overwriteTargetReports
        })
            .then((): void => {
                displaySuccessMessage(language.get(UsersListLanguage.UsersList_CopyReports_Success))
                onCompleted?.()
                handleCloseDialog()
            })
            .catch(displayError)
            .finally((): void => {
                setSubmitting(false)
            })
    }, [
        handleCloseDialog,
        language,
        onCompleted,
        overwriteTargetReports,
        selectedReports,
        selectedTargets,
        sourceUsername
    ])

    const selectedReportNames: string[] = useMemo(
        (): string[] =>
            selectedReports.map((report: CopyReportsReportTableRow): string =>
                isStringNotEmpty(report.ReportName) ? report.ReportName : report.Description
            ),
        [selectedReports]
    )

    const selectedTargetNames: string[] = useMemo(
        (): string[] =>
            selectedTargets.map((target: CopyReportsTargetTableRow): string =>
                isStringNotEmpty(target.FullName) ? `${target.Username} - ${target.FullName}` : target.Username
            ),
        [selectedTargets]
    )

    const dialogTitle: ReactElement = LanguageUtils.formatLanguageEntry(
        language.getInterpolated(
            UsersListLanguage.UsersList_CopyReports_Dialog_Title,
            sourceDisplayLabel,
            sourceUsername
        )
    )

    const stepSectionStyle = (stepIndex: number): React.CSSProperties => ({
        display: activeStep === stepIndex ? 'flex' : 'none',
        flexDirection: 'column',
        gap: 16,
        flex: 1,
        minHeight: 0
    })

    return (
        <Modal
            isOpen={dialogOpen}
            onClose={handleCloseDialog}
            className='copy-reports-wizard-dialog'
            isClosableWithXButton
            onExited={onExited}
        >
            <DialogTitle disableTypography>
                <Typography component='div' variant='h6'>
                    {dialogTitle}
                </Typography>
            </DialogTitle>
            <DialogContent className='copy-reports-wizard-content'>
                <Stepper activeStep={activeStep} className='copy-reports-wizard-stepper'>
                    {stepLabels.map(
                        (label: string): ReactElement => (
                            <Step key={label}>
                                <StepLabel StepIconComponent={CopyReportsWizardStepIcon}>{label}</StepLabel>
                            </Step>
                        )
                    )}
                </Stepper>
                {listLoading ? (
                    <div className='copy-reports-wizard-loader'>
                        <Loader />
                    </div>
                ) : (
                    <></>
                )}
                {!listLoading ? (
                    <>
                        <div style={stepSectionStyle(0)}>
                            <Typography component='div' variant='body2'>
                                {LanguageUtils.formatLanguageEntry(
                                    language.getInterpolated(
                                        UsersListLanguage.UsersList_CopyReports_Intro_Reports,
                                        sourceDisplayLabel
                                    )
                                )}
                            </Typography>
                            {reportRows.length === 0 ? (
                                <Typography variant='body2'>
                                    {language.get(UsersListLanguage.UsersList_CopyReports_Empty_Reports)}
                                </Typography>
                            ) : (
                                <div className={COPY_REPORTS_WIZARD_TABLE_CLASS}>
                                    <DataTable<CopyReportsReportTableRow, keyof CopyReportsReportTableRow>
                                        permissionName={AdminBackendPermissionNames.USER}
                                        toolbarDisabled
                                        columns={reportColumns}
                                        data={reportRows}
                                        isSelectable
                                        localeText={{
                                            noRows: language.get(UsersListLanguage.UsersList_CopyReports_Empty_Reports)
                                        }}
                                        onSelectionChange={setSelectedReports}
                                    />
                                </div>
                            )}
                        </div>
                        <div style={stepSectionStyle(1)}>
                            <Typography variant='body2'>
                                {language.get(UsersListLanguage.UsersList_CopyReports_Intro_Targets)}
                            </Typography>
                            {targetRows.length === 0 ? (
                                <Typography variant='body2'>
                                    {language.get(UsersListLanguage.UsersList_CopyReports_Empty_Targets)}
                                </Typography>
                            ) : (
                                <div className={COPY_REPORTS_WIZARD_TABLE_CLASS}>
                                    <DataTable<CopyReportsTargetTableRow, keyof CopyReportsTargetTableRow>
                                        permissionName={AdminBackendPermissionNames.USER}
                                        toolbarDisabled
                                        columns={targetColumns}
                                        data={targetRows}
                                        isSelectable
                                        localeText={{
                                            noRows: language.get(UsersListLanguage.UsersList_CopyReports_Empty_Targets)
                                        }}
                                        onSelectionChange={setSelectedTargets}
                                    />
                                </div>
                            )}
                        </div>
                        <div style={stepSectionStyle(2)} className={COPY_REPORTS_WIZARD_CONFIRMATION_CLASS}>
                            <Typography
                                component='div'
                                variant='body2'
                                className={COPY_REPORTS_WIZARD_CONFIRMATION_TITLE_CLASS}
                            >
                                {LanguageUtils.formatLanguageEntry(
                                    language.getInterpolated(
                                        selectedReports.length === 1
                                            ? UsersListLanguage.UsersList_CopyReports_Confirm_Report_Label
                                            : UsersListLanguage.UsersList_CopyReports_Confirm_Reports_Label,
                                        selectedReports.length
                                    )
                                )}
                            </Typography>
                            <ul className={COPY_REPORTS_WIZARD_CONFIRMATION_LIST_CLASS}>
                                {selectedReportNames.map(
                                    (reportName: string): ReactElement => (
                                        <li key={reportName}>{reportName}</li>
                                    )
                                )}
                            </ul>
                            <Typography
                                component='div'
                                variant='body2'
                                className={COPY_REPORTS_WIZARD_CONFIRMATION_TITLE_CLASS}
                            >
                                {LanguageUtils.formatLanguageEntry(
                                    language.getInterpolated(
                                        selectedTargets.length === 1
                                            ? UsersListLanguage.UsersList_CopyReports_Confirm_Target_Label
                                            : UsersListLanguage.UsersList_CopyReports_Confirm_Targets_Label,
                                        selectedTargets.length
                                    )
                                )}
                            </Typography>
                            <ul className={COPY_REPORTS_WIZARD_CONFIRMATION_LIST_CLASS}>
                                {selectedTargetNames.map(
                                    (targetName: string): ReactElement => (
                                        <li key={targetName}>{targetName}</li>
                                    )
                                )}
                            </ul>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={overwriteTargetReports}
                                        onChange={(_event, checked: boolean): void =>
                                            setOverwriteTargetReports(checked)
                                        }
                                        color='primary'
                                    />
                                }
                                label={language.get(UsersListLanguage.UsersList_CopyReports_Overwrite_Label)}
                            />
                        </div>
                    </>
                ) : (
                    <></>
                )}
            </DialogContent>
            <DialogActions className='copy-reports-wizard-actions'>
                <RoundedButton onClick={handleCloseDialog} isOutlined disabled={submitting} type='button' shrink>
                    {language.get(UsersListLanguage.UsersList_CopyReports_Cancel)}
                </RoundedButton>
                {activeStep > 0 ? (
                    <RoundedButton onClick={handleBack} isOutlined disabled={submitting} type='button' shrink>
                        {language.get(UsersListLanguage.UsersList_CopyReports_Back)}
                    </RoundedButton>
                ) : (
                    <></>
                )}
                {activeStep < WIZARD_STEP_COUNT - 1 ? (
                    <RoundedButton
                        onClick={handleNext}
                        type='button'
                        shrink
                        disabled={
                            submitting ||
                            (activeStep === 0 &&
                                (ArrayUtils.isEmpty(reportRows) || ArrayUtils.isEmpty(selectedReports))) ||
                            (activeStep === 1 &&
                                (ArrayUtils.isEmpty(targetRows) || ArrayUtils.isEmpty(selectedTargets)))
                        }
                    >
                        {language.get(UsersListLanguage.UsersList_CopyReports_Next)}
                    </RoundedButton>
                ) : (
                    <RoundedButton
                        onClick={handleExecuteCopy}
                        type='button'
                        shrink
                        disabled={
                            submitting ||
                            ArrayUtils.isEmpty(selectedReports) ||
                            ArrayUtils.isEmpty(selectedTargets) ||
                            ArrayUtils.isEmpty(reportRows) ||
                            ArrayUtils.isEmpty(targetRows)
                        }
                    >
                        {language.get(UsersListLanguage.UsersList_CopyReports_Copy)}
                    </RoundedButton>
                )}
            </DialogActions>
        </Modal>
    )
}
